import { useHistory, useRouteMatch } from 'react-router-dom';
import { v4 as uuid } from 'uuid';
import { getLocalName } from 'vet-bones/utils';
import { createSelectItem } from 'vet-bones/utils/select';

import { PROJECT_MAPPING_EXPAND_MAPPINGS_PARAM } from 'src/constants/project';
import { ROUTE } from 'src/constants/routes';
import { RelationshipDirection } from 'src/containers/ProjectDiagram/constants/modelConcept';
import { useGenerateNewIri } from 'src/containers/ProjectDiagram/hooks/entityDrawer/useGenerateNewIri';
import { addAttributeItemToClass } from 'src/containers/ProjectDiagram/hooks/entityDrawer/useModelConceptAttributeForClass';
import { useDiagram } from 'src/containers/ProjectDiagram/hooks/useDiagramValue';
import {
  Mapping,
  MappingField,
  MappingIdentifierPart,
  MappingIdentifierType,
  Resource,
} from 'src/containers/ProjectDiagram/types/data';
import { ModelData } from 'src/containers/ProjectDiagram/types/model';
import {
  DiagramModelCommitLayout,
  commitDiagramModel,
} from 'src/containers/ProjectDiagram/utils/commitDiagramModel';
import { createDraftData } from 'src/containers/ProjectDiagram/utils/draft/createDraftData';
import { setLabelForIri } from 'src/containers/ProjectDiagram/utils/labels';
import { addMapping } from 'src/containers/ProjectDiagram/utils/mapping/addMapping';
import { addRelationshipMapping } from 'src/containers/ProjectDiagram/utils/mapping/addRelationshipMapping';
import { addModelConceptClass } from 'src/containers/ProjectDiagram/utils/modelConceptClass/addModelConceptClass';
import { getModelConceptClassKey } from 'src/containers/ProjectDiagram/utils/modelConceptClass/createModelConceptClass';
import { addRelationshipData } from 'src/containers/ProjectDiagram/utils/modelConceptRelationship/addModelConceptRelationship';
import {
  getResourceKey,
  getResourceValue,
} from 'src/containers/ProjectDiagram/utils/resource/createResource';
import { setDataProperty } from 'src/containers/ProjectDiagram/utils/setDataProperty';
import {
  RelationshipSuggestion,
  Suggestion,
  SuggestionType,
} from 'src/containers/ProjectMapping/types';
import { MappingSuggestion } from 'src/containers/ProjectResourceSuggestionsDialog/projectResourceSuggestionsData';
import { uiSlice } from 'src/store/ui/ui.slice';
import { XSD_STRING_IRI } from 'src/templates/queries/iris';
import { useNavigateMapping } from 'src/utils/hooks/useNavigateMapping';
import { useReduxDispatch } from 'src/utils/hooks/useReduxDispatch';

export const useCreateMappings = () => {
  const diagram = useDiagram();
  const dispatch = useReduxDispatch();
  const generateNewIri = useGenerateNewIri();
  const history = useHistory();
  const isUsingMappingRoute = Boolean(useRouteMatch(ROUTE.PROJECT_MAPPING));
  const navigateMapping = useNavigateMapping();

  return (mappingSuggestions: MappingSuggestion[] | null) => {
    if (!diagram || !mappingSuggestions) {
      return;
    }

    let lastMappingUuid = null;
    let numMappingsAdded = 0;

    // Create mappings from suggestions
    commitDiagramModel(
      diagram,
      (model, diagram) => {
        // setup draft data
        if (!model.modelData.draftData) {
          const draftData = createDraftData(model);
          setDataProperty<ModelData>(model)(
            model.modelData,
            'draftData',
            draftData
          );
        }

        // record is resource id -> class iri -> class mapping key
        const createdClassMappingsByResource: Record<
          string,
          Record<string, Mapping>
        > = {};

        const classesToCreate: Record<string, { iri: string; label: string }> =
          {};
        const getCreatedClassIri = (iri: string) =>
          classesToCreate[iri]?.iri || iri;

        const propertyIrisToCreate: Record<string, string> = {};
        const getCreatedPropertyIri = (iri: string) =>
          propertyIrisToCreate[iri] || iri;

        const relationshipMappingsToCreate: RelationshipSuggestion[] = [];

        mappingSuggestions.forEach((mappingSuggestion) => {
          if (!mappingSuggestion.isClassAutoCreated) {
            return;
          }

          // create a new iri for the class if not yet generated
          if (!classesToCreate[mappingSuggestion.classIri]) {
            const newClassIri = generateNewIri(mappingSuggestion.classIri);
            classesToCreate[mappingSuggestion.classIri] = {
              iri: newClassIri,
              label: mappingSuggestion.classLabel,
            };
          }

          mappingSuggestion.suggestions.forEach((suggestion) => {
            if (
              suggestion.type === SuggestionType.RELATIONSHIP &&
              suggestion.propertyType
            ) {
              // create a new iri for the opposing class if not yet generated
              if (!classesToCreate[suggestion.propertyType]) {
                classesToCreate[suggestion.propertyType] = {
                  iri: generateNewIri(suggestion.propertyType),
                  label:
                    suggestion.propertyTypeLabel ||
                    getLocalName(suggestion.propertyType),
                };
              }

              // create a new iri for the property if not yet generated
              if (!propertyIrisToCreate[suggestion.iri]) {
                propertyIrisToCreate[suggestion.iri] = generateNewIri(
                  suggestion.iri
                );
              }
            } else if (suggestion.type === SuggestionType.ATTRIBUTE) {
              // create a new iri for the property if not yet generated
              if (!propertyIrisToCreate[suggestion.iri]) {
                propertyIrisToCreate[suggestion.iri] = generateNewIri(
                  suggestion.iri
                );
              }
            }
          });
        });

        mappingSuggestions.forEach((mappingSuggestion) => {
          const resourceData = model.nodeDataArray.find(
            (nodeData): nodeData is Resource =>
              nodeData.key === getResourceKey(mappingSuggestion.resourceId)
          );
          if (!resourceData) {
            // skip missing resources
            return;
          }
          const fields = resourceData.fields.reduce<
            Record<string, MappingField>
          >((acc, { value, label }) => {
            acc[value] = { value, label, targets: [] };
            return acc;
          }, {});

          let labelField: string | undefined;
          const primaryIdentifierParts: MappingIdentifierPart[] = [];

          const hasField = (field: string) => Boolean(fields[field]);
          const hasFields = (fields: MappingIdentifierPart[]) =>
            fields.every(
              (field) =>
                field.type !== MappingIdentifierType.FIELD ||
                hasField(field.value)
            );

          // Populate field targets for each suggestion
          mappingSuggestion.suggestions.forEach((suggestion: Suggestion) => {
            if (suggestion.rejected) {
              // skip rejected suggestions
              return;
            }

            if (
              suggestion.type === SuggestionType.PRIMARY_IDENTIFIER &&
              hasFields(suggestion.fields)
            ) {
              suggestion.fields.forEach((primaryIdentifierFields) => {
                primaryIdentifierParts.push(primaryIdentifierFields);
              });
            } else if (
              suggestion.type === SuggestionType.LABEL &&
              hasField(suggestion.field)
            ) {
              labelField = suggestion.field;
            } else if (
              suggestion.type === SuggestionType.ATTRIBUTE &&
              hasField(suggestion.field)
            ) {
              fields[suggestion.field].targets.push(
                getCreatedPropertyIri(suggestion.iri)
              );
            } else if (
              suggestion.type === SuggestionType.RELATIONSHIP &&
              suggestion.propertyType &&
              hasFields(suggestion.fields)
            ) {
              relationshipMappingsToCreate.push(suggestion);
            }
          });

          const classIri = getCreatedClassIri(mappingSuggestion.classIri);

          if (mappingSuggestion.isClassAutoCreated) {
            // create the new model concept class
            addModelConceptClass(classesToCreate[mappingSuggestion.classIri])(
              model
            );

            mappingSuggestion.suggestions.forEach((suggestion) => {
              if (suggestion.rejected) {
                return;
              }

              if (suggestion.type === SuggestionType.ATTRIBUTE) {
                const { iri, label, propertyType } = suggestion;
                const attributePropertyType = propertyType || XSD_STRING_IRI;
                // create the new class attribute
                addAttributeItemToClass(classIri, {
                  text: label || getLocalName(iri),
                  value: getCreatedPropertyIri(iri),
                  isNewlyCreated: true,
                  ranges: [
                    createSelectItem(
                      attributePropertyType,
                      attributePropertyType
                    ),
                  ],
                })(model);
              }

              if (
                suggestion.type === SuggestionType.RELATIONSHIP &&
                suggestion.propertyType
              ) {
                const { iri, label, propertyType, direction } = suggestion;
                const [fromIri, toIri] =
                  direction === RelationshipDirection.OUTGOING
                    ? [classIri, getCreatedClassIri(propertyType)]
                    : [getCreatedClassIri(propertyType), classIri];

                // create the new opposing class
                addModelConceptClass(classesToCreate[propertyType])(model);
                // create the new relationship
                addRelationshipData({
                  fromIri,
                  value: getCreatedPropertyIri(iri),
                  toIri,
                  model,
                });
                // set the label for the new relationship (not done in addRelationshipData)
                setLabelForIri(
                  getCreatedPropertyIri(iri),
                  label || getLocalName(iri)
                )(model);
              }
            });
          }

          lastMappingUuid = uuid();
          numMappingsAdded += 1;

          // add the new class mapping
          const { mapping } = addMapping({
            fromId: mappingSuggestion.resourceId,
            toKey: getModelConceptClassKey(classIri),
            text: mappingSuggestion.name,
            fields,
            label: labelField,
            primaryIdentifier: {
              baseIri: '',
              parts: primaryIdentifierParts,
            },
            value: lastMappingUuid,
          })(model, diagram);
          if (mapping) {
            const resourceId = getResourceValue(resourceData.key);
            if (!createdClassMappingsByResource[resourceId]) {
              createdClassMappingsByResource[resourceId] = {};
            }
            createdClassMappingsByResource[resourceId][
              // key on the orignial classIri
              mappingSuggestion.classIri
            ] = mapping;
          }
        });
        relationshipMappingsToCreate.forEach((suggestion) => {
          const { resource, propertyType } = suggestion;
          const classMapping =
            createdClassMappingsByResource[resource.id]?.[suggestion.classIri];
          if (!classMapping || !propertyType) {
            return;
          }

          // add the new relationship
          if (suggestion.direction === RelationshipDirection.OUTGOING) {
            addRelationshipMapping({
              from: classMapping.key,
              relationshipIri: getCreatedPropertyIri(suggestion.iri),
              toClassIri: getCreatedClassIri(propertyType),
              toPrimaryIdentifier: {
                baseIri: '',
                parts: [...suggestion.fields],
              },
            })(model);
          } else {
            addRelationshipMapping({
              fromClassIri: getCreatedClassIri(propertyType),
              fromPrimaryIdentifier: {
                baseIri: '',
                parts: [...suggestion.fields],
              },
              relationshipIri: getCreatedPropertyIri(suggestion.iri),
              to: classMapping.key,
            })(model);
          }
        });
      },
      { layout: DiagramModelCommitLayout.PARTIAL }
    );
    diagram.updateAllTargetBindings();
    diagram.layout.doLayout(diagram);

    dispatch(
      uiSlice.actions.toggledResourceSuggestionsDialog({
        isOpen: false,
      })
    );

    if (isUsingMappingRoute) {
      if (numMappingsAdded === 1) {
        // Navigate to new mapping when only one was created
        navigateMapping(lastMappingUuid, false);
      }

      // Expand mappings in sidebar
      history.replace(
        `${history.location.pathname}?${PROJECT_MAPPING_EXPAND_MAPPINGS_PARAM}`
      );
    }
  };
};
